import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import type { UnitNotes } from "../types";

const precalculusUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Polynomial and Rational Functions",
  examWeight: "30–40%",
  focus: "Covariation, Rates of Change, Asymptotic Behavior",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Change in Tandem",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Covariation</h3>
            <KeyTerm 
              term="Covariation" 
              definition="Analyzing how changes in the input (Δx) result in changes in the output (Δy), rather than just looking at static points."
            />
            <p className="text-slate-700 leading-relaxed mb-4">
              Instead of examining static points <InlineMath math="(x, y)" />, we focus on how changes in <InlineMath math="x" /> (<InlineMath math="\Delta x" />) result in changes in <InlineMath math="y" /> (<InlineMath math="\Delta y" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Monotonicity (Increasing/Decreasing)</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Increasing</h4>
                <p className="text-sm text-slate-700 mt-1">As input values increase, output values increase.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Notation:</em> If <InlineMath math="a < b" />, then <InlineMath math="f(a) < f(b)" /></p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Decreasing</h4>
                <p className="text-sm text-slate-700 mt-1">As input values increase, output values decrease.</p>
                <p className="text-sm text-slate-600 mt-2"><em>Notation:</em> If <InlineMath math="a < b" />, then <InlineMath math="f(a) > f(b)" /></p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concavity</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Concavity describes the rate of change <em>of</em> the rate of change.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Concave Up</h4>
                <p className="text-sm text-slate-700 mt-1">The rate of change is <strong>increasing</strong>. Graph bends upward (like a cup ∪).</p>
                <p className="text-sm text-slate-600 mt-2"><em>Scenarios:</em> Increasing at an increasing rate, or decreasing at a decreasing rate.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Concave Down</h4>
                <p className="text-sm text-slate-700 mt-1">The rate of change is <strong>decreasing</strong>. Graph bends downward (like a frown ∩).</p>
                <p className="text-sm text-slate-600 mt-2"><em>Scenarios:</em> Increasing at a decreasing rate, or decreasing at an increasing rate.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Rates of Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Average Rate of Change (AROC)</h3>
            <KeyTerm 
              term="Average Rate of Change (AROC)" 
              definition="The slope of the secant line connecting two points on a curve. Measures how much the output changes, on average, for every 1 unit of input change."
            />
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 my-4">
              <p className="font-semibold text-slate-800">Formula:</p>
              <BlockMath math="\text{AROC} = \frac{f(b) - f(a)}{b - a}" />
              <p className="text-sm text-slate-600 mt-2">over the interval <InlineMath math="[a, b]" /></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rate of Change at a Point</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Estimated by taking the AROC over smaller and smaller intervals around a specific point.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-green-50 p-3 rounded-lg border border-green-200 text-center">
                <p className="font-semibold text-green-800">Positive Rate</p>
                <p className="text-sm text-slate-700">Function is increasing</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200 text-center">
                <p className="font-semibold text-red-800">Negative Rate</p>
                <p className="text-sm text-slate-700">Function is decreasing</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-center">
                <p className="font-semibold text-amber-800">Zero Rate</p>
                <p className="text-sm text-slate-700">Local max or min</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Rates of Change in Linear and Quadratic Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Linear Functions</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Characteristics</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li>Have a <strong>constant</strong> rate of change</li>
                <li><strong>First differences</strong> (change in <InlineMath math="y" /> for equal steps in <InlineMath math="x" />) are constant</li>
                <li>Graph: A straight line</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Quadratic Functions</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-800">Characteristics</h4>
              <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 mt-2">
                <li>Have a <strong>linear</strong> rate of change (the rate of change itself changes at a constant rate)</li>
                <li><strong>Second differences</strong> are constant</li>
                <li>Graph: A parabola (Concave Up or Down)</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Strict Concavity Rule</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Concave Up</h4>
                <p className="text-sm text-slate-700 mt-1">AROC <strong>increases</strong> over equal-length intervals</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Concave Down</h4>
                <p className="text-sm text-slate-700 mt-1">AROC <strong>decreases</strong> over equal-length intervals</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Polynomial Functions and Rates of Change",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Polynomial Definition</h3>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <BlockMath math="p(x) = a_n x^n + a_{n-1} x^{n-1} + \dots + a_1 x + a_0" />
              <p className="text-sm text-slate-600 mt-2">where <InlineMath math="n" /> is a non-negative integer and coefficients are real numbers.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Extrema (Max/Min)</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <KeyTerm 
                term="Local (Relative) Extrema" 
                definition="Points where the function switches from increasing to decreasing (max) or decreasing to increasing (min)."
              />
              <KeyTerm 
                term="Absolute (Global) Extrema" 
                definition="The highest or lowest point over the entire domain."
              />
            </div>
            <ImportantNote>
              <strong>Key Rule:</strong> Between any two distinct real zeros, a polynomial must have at least one extremum.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Inflection Points</h3>
            <KeyTerm 
              term="Inflection Point" 
              definition="A point where the concavity changes (from Up to Down or Down to Up). Occurs where the rate of change switches from increasing to decreasing (or vice versa)."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Polynomial Functions and Complex Zeros",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Fundamental Theorem of Algebra</h3>
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 mb-4">
              <p className="text-slate-700">
                A polynomial of degree <InlineMath math="n" /> has exactly <InlineMath math="n" /> complex zeros (counting multiplicity).
              </p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Zeros and Linear Factors</h3>
            <ul className="list-disc pl-5 space-y-2 text-slate-700 mb-4">
              <li>If <InlineMath math="x = a" /> is a root, then <InlineMath math="(x-a)" /> is a factor.</li>
              <li>If <InlineMath math="x = a" /> is a real root, the graph touches or crosses the x-axis at <InlineMath math="a" />.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Multiplicity</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Odd Multiplicity</h4>
                <p className="text-sm text-slate-700 mt-1">The graph <strong>crosses</strong> the x-axis at the root.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Even Multiplicity</h4>
                <p className="text-sm text-slate-700 mt-1">The graph <strong>touches</strong> the x-axis and turns around.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Complex Conjugate Theorem</h3>
            <ImportantNote>
              If a polynomial has <strong>real coefficients</strong> and <InlineMath math="a + bi" /> is a zero, then its conjugate <InlineMath math="a - bi" /> must also be a zero.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Determining Degree from Data</h3>
            <Mnemonic 
              title="Finite Differences Method"
              content="Look at successive differences of outputs for equal-interval inputs. If the n-th differences are constant, the polynomial is degree n."
            />
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Polynomial Functions and End Behavior",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Leading Term Test</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The term with the highest degree (<InlineMath math="a_n x^n" />) dominates the behavior for large magnitude inputs.
            </p>
            <p className="text-slate-700 mb-4">
              <em>Notation:</em> Use limit notation, e.g., <InlineMath math="\lim_{x \to \infty} f(x) = \infty" />
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Four Scenarios</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Even Degree, Positive Lead</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\lim_{x \to \infty} = \infty" />, <InlineMath math="\lim_{x \to -\infty} = \infty" /></p>
                <p className="text-sm text-slate-600">(Up/Up)</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Even Degree, Negative Lead</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\lim_{x \to \infty} = -\infty" />, <InlineMath math="\lim_{x \to -\infty} = -\infty" /></p>
                <p className="text-sm text-slate-600">(Down/Down)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Odd Degree, Positive Lead</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\lim_{x \to \infty} = \infty" />, <InlineMath math="\lim_{x \to -\infty} = -\infty" /></p>
                <p className="text-sm text-slate-600">(Down/Up)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Odd Degree, Negative Lead</h4>
                <p className="text-sm text-slate-700 mt-1"><InlineMath math="\lim_{x \to \infty} = -\infty" />, <InlineMath math="\lim_{x \to -\infty} = \infty" /></p>
                <p className="text-sm text-slate-600">(Up/Down)</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Rational Functions and End Behavior",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rational Functions</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Ratios of polynomials: <InlineMath math="r(x) = \frac{p(x)}{q(x)}" />
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Horizontal Asymptotes</h3>
            <p className="text-slate-700 mb-4">
              Determined by comparing the degree of the numerator (<InlineMath math="n" />) and the denominator (<InlineMath math="m" />):
            </p>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Top Heavy (<InlineMath math="n > m" />)</h4>
                <p className="text-sm text-slate-700 mt-1">No Horizontal Asymptote. If <InlineMath math="n = m + 1" />, there is a <strong>Slant (Oblique) Asymptote</strong>.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Bottom Heavy (<InlineMath math="n < m" />)</h4>
                <p className="text-sm text-slate-700 mt-1">HA at <InlineMath math="y = 0" />. <InlineMath math="\lim_{x \to \infty} f(x) = 0" /></p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Balanced (<InlineMath math="n = m" />)</h4>
                <p className="text-sm text-slate-700 mt-1">HA at <InlineMath math="y = \frac{\text{leading coeff numerator}}{\text{leading coeff denominator}}" /></p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-8",
      title: "Topic 1.8: Rational Functions and Zeros",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding Zeros (X-Intercepts)</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <p className="text-slate-700">
                Set the <strong>numerator</strong> equal to zero and solve for <InlineMath math="x" />.
              </p>
            </div>
            <ImportantNote>
              <strong>Crucial Check:</strong> Ensure the solution does <strong>not</strong> make the denominator zero. If it does, it's a hole, not an intercept.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Solving Inequalities</h3>
            <p className="text-slate-700 leading-relaxed">
              The zeros of both the numerator and denominator divide the number line into intervals. The sign of the function can only change at these points.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-9",
      title: "Topic 1.9: Rational Functions and Vertical Asymptotes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding Vertical Asymptotes (VA)</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <p className="text-slate-700">
                Set the <strong>denominator</strong> equal to zero. Any value <InlineMath math="x = a" /> that makes the denominator zero <em>but not the numerator</em> is a VA.
              </p>
            </div>
            <ImportantNote>
              <strong>Multiplicity Check:</strong> If <InlineMath math="x = a" /> is a zero of both numerator and denominator, it is a VA only if the multiplicity in the denominator is <strong>greater</strong> than in the numerator.
            </ImportantNote>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Behavior near VA</h3>
            <p className="text-slate-700 mb-4">
              As <InlineMath math="x \to a" />, <InlineMath math="f(x) \to \infty" /> or <InlineMath math="-\infty" />.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700">
                <strong>Limit Notation:</strong> <InlineMath math="\lim_{x \to a^+} f(x) = \infty" /> (approaching from right) or <InlineMath math="\lim_{x \to a^-} f(x) = -\infty" /> (approaching from left).
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-10",
      title: "Topic 1.10: Rational Functions and Holes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition of a Hole</h3>
            <KeyTerm 
              term="Hole (Removable Discontinuity)" 
              definition="Exists at x = c if (x - c) is a factor of BOTH the numerator and denominator (and the multiplicity in the numerator ≥ the denominator)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Finding the Coordinate of the Hole</h3>
            <ol className="list-decimal pl-5 space-y-2 text-slate-700">
              <li>Factor numerator and denominator.</li>
              <li>Cancel the common factor <InlineMath math="(x - c)" />.</li>
              <li>Substitute <InlineMath math="x = c" /> into the <em>simplified</em> version to find the <InlineMath math="y" />-value.</li>
            </ol>
            <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 mt-4">
              <p className="text-slate-700">
                <strong>Limit Notation:</strong> <InlineMath math="\lim_{x \to c} f(x) = L" /> (The limit exists, even though the function is undefined at <InlineMath math="c" />).
              </p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-11",
      title: "Topic 1.11: Equivalent Representations",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Polynomial Long Division</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Used to rewrite improper rational fractions (degree top ≥ degree bottom).
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-slate-700">
                Form: <InlineMath math="\frac{p(x)}{q(x)} = \text{Quotient} + \frac{\text{Remainder}}{q(x)}" />
              </p>
              <p className="text-sm text-slate-600 mt-2">Helpful for finding Slant Asymptotes.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Binomial Expansion</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Expanding <InlineMath math="(a+b)^n" /> using Pascal's Triangle (coefficients are combinations <InlineMath math="_nC_r" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Factored Forms</h3>
            <ImportantNote>
              Factored forms are crucial for quickly identifying intercepts, asymptotes, and holes.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-12",
      title: "Topic 1.12: Transformations of Functions",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">General Transformation Form</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <BlockMath math="g(x) = A \cdot f(B(x - H)) + K" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Translations (Additions)</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Inside (<InlineMath math="x - H" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Horizontal shift. Note the sign flip!</p>
                <p className="text-sm text-slate-600 mt-1"><InlineMath math="x - 3" /> shifts Right 3</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Outside (<InlineMath math="+K" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Vertical shift.</p>
                <p className="text-sm text-slate-600 mt-1"><InlineMath math="+3" /> shifts Up 3</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Dilations (Multiplications)</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Outside (<InlineMath math="A" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Vertical stretch/compression.</p>
                <p className="text-sm text-slate-600 mt-1">If <InlineMath math="A < 0" />, reflect over x-axis.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Inside (<InlineMath math="B" />)</h4>
                <p className="text-sm text-slate-700 mt-1">Horizontal stretch/compression by <InlineMath math="1/B" />.</p>
                <p className="text-sm text-slate-600 mt-1">If <InlineMath math="B < 0" />, reflect over y-axis.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-13",
      title: "Topic 1.13: Function Model Selection",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Choosing the Right Model</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Linear</h4>
                <p className="text-sm text-slate-700">Constant rate of change.</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Quadratic</h4>
                <p className="text-sm text-slate-700">Symmetric data with a max/min; rate of change is linear. Area problems.</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">Cubic</h4>
                <p className="text-sm text-slate-700">Volume problems; data changes concavity once.</p>
              </div>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Higher Degree Polynomial</h4>
                <p className="text-sm text-slate-700">Data oscillates multiple times.</p>
              </div>
              <div className="bg-red-50 p-3 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Piecewise</h4>
                <p className="text-sm text-slate-700">Different behaviors over different intervals of the domain.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Stating Assumptions</h3>
            <ImportantNote>
              When modeling, you must state limitations (e.g., "model is valid only for <InlineMath math="t > 0" />," or "population cannot be negative").
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-14",
      title: "Topic 1.14: Function Model Construction and Application",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Building Models</h3>
            <KeyTerm 
              term="Regression" 
              definition="Using technology to find the line/curve of best fit."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Inverse Variation</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Modeled by rational functions: <InlineMath math="y = \frac{k}{x^n}" />
            </p>
            <p className="text-sm text-slate-600">
              Example: Gravity or magnetic force (inverse square laws).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Data Points Required</h3>
            <Mnemonic 
              title="Points for Polynomial"
              content="n + 1 data points are required to uniquely determine a polynomial of degree n. (e.g., 2 points for a line, 3 for a quadratic)."
            />
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpolation vs. Extrapolation</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Interpolation</h4>
                <p className="text-sm text-slate-700 mt-1">Find values <strong>inside</strong> the data range. Generally more reliable.</p>
              </div>
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Extrapolation</h4>
                <p className="text-sm text-slate-700 mt-1">Predict values <strong>outside</strong> the data range. Less reliable, use caution.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "summary-1",
      title: "Unit 1 Summary: Key Skills",
      content: (
        <div className="space-y-6">
          <section>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <span className="bg-blue-200 text-blue-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Rates of Change</h4>
                  <p className="text-sm text-slate-700 mt-1">Calculate AROC using <InlineMath math="\frac{f(b)-f(a)}{b-a}" /> and interpret in context.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">End Behavior</h4>
                  <p className="text-sm text-slate-700 mt-1">Use the leading term to determine limits as <InlineMath math="x \to \pm\infty" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                <span className="bg-purple-200 text-purple-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Asymptotes and Holes</h4>
                  <p className="text-sm text-slate-700 mt-1">Factor and compare numerator/denominator zeros to find VAs, HAs, and holes.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Transformations</h4>
                  <p className="text-sm text-slate-700 mt-1">Apply <InlineMath math="A \cdot f(B(x-H))+K" /> to shift and stretch graphs.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">5</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Model Selection</h4>
                  <p className="text-sm text-slate-700 mt-1">Choose linear, quadratic, polynomial, or piecewise based on data patterns.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default precalculusUnit1Notes;
